import React, { useCallback, useContext, useEffect, useState } from "react";
import CustomInput from "../../components/CustomInput";
import { Editor } from "primereact/editor";
import DragDrop from "../../components/DragDrop";
import CustomSelect from "../../components/CustomSelect";
import { categoryService } from "../../../../services/api/categoryService";
import { proceduceService } from "../../../../services/api/procedureService";
import { firebaseService } from "../../../../services/api/firebaseService";
import { AuthContext } from "../../../../contexts/AuthContext";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import Modal from "../../components/Modal";

const isHtmlString = (val) =>
  typeof val === "string" && val.trim().startsWith("<");

const isHttpUrl = (val) => typeof val === "string" && /^https?:\/\//i.test(val);

const isFirebaseUrl = (val) =>
  typeof val === "string" && val.startsWith("https://storage.googleapis.com/");

const getFileNameFromUrl = (url = "") => {
  if (!url || typeof url !== "string") return "";
  try {
    const cleanUrl = url.split("?")[0];
    return decodeURIComponent(cleanUrl.split("/").pop() || "");
  } catch {
    return "";
  }
};

const ProcedureFormPopup = ({
  mode = "create",
  procedureId = null,
  closePopup,
  onSuccess,
}) => {
  const { token } = useContext(AuthContext);
  const isEditMode = mode === "edit";

  const [catLoading, setCatLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(isEditMode);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    name: "",
    content: "",
    category: null,
    submissionTime: "",
    reviewTime: "",
    pdfFile: null,
  });

  const [existingPdfUrl, setExistingPdfUrl] = useState("");
  const [isEditingPdf, setIsEditingPdf] = useState(false);

  const [errors, setErrors] = useState({
    name: "",
    category: "",
    pdfFile: "",
  });

  const fetchCategories = useCallback(async () => {
    try {
      setCatLoading(true);
      const res = await categoryService.get("", token);
      const opts = Array.isArray(res?.categories)
        ? res.categories.map((c) => ({
          value: c.id,
          label: c.name,
        }))
        : [];
      setCategories(opts);
      return opts;
    } catch {
      toast.error("Không tải được danh sách thể loại.");
      return [];
    } finally {
      setCatLoading(false);
    }
  }, [token]);

  const fetchProcedureData = useCallback(
    async (cats) => {
      if (!procedureId) return;
      try {
        setDataLoading(true);
        const res = await proceduceService.get_by_id(procedureId, token);
        const detail = res?.data || {};

        let content = "";
        if (detail.txtUrl) {
          if (isHtmlString(detail.txtUrl)) {
            content = detail.txtUrl;
          } else if (isHttpUrl(detail.txtUrl)) {
            const r = await fetch(detail.txtUrl);
            content = await r.text();
          }
        }

        const catId = detail?.categoryId?._id || detail?.categoryId;
        const catObj = catId
          ? {
            value: catId,
            label: cats.find((c) => c.value === catId)?.label || "",
          }
          : null;

        setForm({
          name: detail.name || "",
          content,
          category: catObj,
          submissionTime: detail.submissionTime || "",
          reviewTime: detail.reviewTime || "",
          pdfFile: null,
        });

        setExistingPdfUrl(detail.fileUrl || "");
        setIsEditingPdf(false);
      } catch {
        toast.error("Không thể tải dữ liệu thủ tục.");
      } finally {
        setDataLoading(false);
      }
    },
    [procedureId, token],
  );

  useEffect(() => {
    const init = async () => {
      const cats = await fetchCategories();
      if (isEditMode) await fetchProcedureData(cats);
    };
    init();
  }, [fetchCategories, fetchProcedureData, isEditMode]);

  const setField = (key, value) => {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((e) => ({ ...e, [key]: "" }));
  };

  const handleContentChange = (e) => setField("content", e.htmlValue || "");

  const validate = () => {
    const next = { name: "", category: "", pdfFile: "" };

    if (!form.name.trim()) next.name = "Vui lòng nhập tên thủ tục.";
    if (!form.category?.value) next.category = "Vui lòng chọn thể loại.";

    if (
      form.pdfFile &&
      !(
        form.pdfFile.type === "application/pdf" ||
        form.pdfFile.name.toLowerCase().endsWith(".pdf")
      )
    ) {
      next.pdfFile = "Chỉ chấp nhận file PDF.";
    }

    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const extractFirebaseImageUrls = (html) => {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, "text/html");
    return Array.from(doc.querySelectorAll("img"))
      .map((i) => i.src)
      .filter(isFirebaseUrl);
  };

  const processImagesInContent = async (html) => {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(html, "text/html");
    const imgs = doc.querySelectorAll("img");

    for (const img of imgs) {
      const src = img.getAttribute("src");
      if (!src || isFirebaseUrl(src)) continue;

      if (src.startsWith("data:image/")) {
        const blob = await (await fetch(src)).blob();
        const file = new File([blob], `editor-${Date.now()}.png`, {
          type: blob.type,
        });
        const up = await firebaseService.upload_file(token, file);
        img.setAttribute("src", up.url);
      }
    }

    return doc.body.innerHTML;
  };

  const onSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);

    try {
      let oldImages = [];
      if (isEditMode) {
        const res = await proceduceService.get_by_id(procedureId, token);
        const old = res?.data?.txtUrl;
        if (isHtmlString(old)) {
          oldImages = extractFirebaseImageUrls(old);
        }
      }

      const processed = await processImagesInContent(form.content);
      const newImages = extractFirebaseImageUrls(processed);

      let pdfUrl = existingPdfUrl;
      if (form.pdfFile) {
        const up = await firebaseService.upload_file(token, form.pdfFile);
        pdfUrl = up.url;
        if (isFirebaseUrl(existingPdfUrl)) {
          await firebaseService.delete_file(token, existingPdfUrl);
        }
      }

      const fd = new FormData();
      fd.append("nameProce", form.name.trim());
      fd.append("txtUrl", processed);
      fd.append("categoryId", form.category.value);
      fd.append("submissionTime", form.submissionTime);
      fd.append("reviewTime", form.reviewTime);
      if (pdfUrl) fd.append("fileUrl", pdfUrl);

      if (isEditMode) {
        await proceduceService.update(procedureId, fd, token);
        toast.success("Cập nhật thủ tục thành công!");
      } else {
        await proceduceService.create(fd, token);
        toast.success("Tạo thủ tục thành công!");
      }

      const toDelete = oldImages.filter((u) => !newImages.includes(u));
      for (const url of toDelete) {
        await firebaseService.delete_file(token, url);
      }

      onSuccess?.();
      closePopup?.();
    } catch {
      toast.error("Lưu thủ tục thất bại.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const beautifyFileName = (s) =>
    (s || "").replace(/%20/g, " ").replace(/\s+/g, "_");

  if (dataLoading) return <Loading />;

  return (
    <Modal
      isOpen
      onClose={closePopup}
      title={isEditMode ? "Chỉnh sửa thủ tục" : "Thêm thủ tục mới"}
      maxWidth="900px"
      showCancelButton
      cancelButtonText="Hủy"
      primaryButtonText={
        isSubmitting
          ? isEditMode
            ? "Đang cập nhật..."
            : "Đang tạo..."
          : isEditMode
            ? "Cập nhật"
            : "Thêm"
      }
      primaryButtonOnClick={onSubmit}
      primaryButtonVariant="primary"
    >
      <form
        onSubmit={(e) => e.preventDefault()}
        className="px-6 py-4 space-y-4"
      >
        <div className="flex flex-col gap-1">
          <strong className="text-gray-700">Tên thủ tục:</strong>
          <CustomInput
            placeholder="Nhập tên thủ tục"
            value={form.name}
            required
            onChange={(v) => setField("name", v)}
          />
          {errors.name && (
            <span className="text-xs text-red-500">{errors.name}</span>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <strong className="text-gray-700">Thể loại:</strong>
            <CustomSelect
              options={categories}
              placeholder={catLoading ? "Đang tải..." : "Loại dịch vụ"}
              value={form.category}
              onChange={(v) => setField("category", v)}
              disabled={catLoading}
              required
            />
            {errors.category && (
              <span className="text-xs text-red-500">{errors.category}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <strong className="text-gray-700">Thời gian nộp hồ sơ:</strong>
            <CustomInput
              value={form.submissionTime}
              onChange={(v) => setField("submissionTime", v)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <strong className="text-gray-700">Thời gian thẩm định:</strong>
            <CustomInput
              value={form.reviewTime}
              onChange={(v) => setField("reviewTime", v)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <strong className="text-gray-700">Miêu tả thủ tục:</strong>
          <Editor
            value={form.content}
            onTextChange={handleContentChange}
            style={{ height: 200 }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <strong className="text-gray-700">File thủ tục (PDF):</strong>

          {isEditMode && existingPdfUrl && !isEditingPdf ? (
            <div className="flex justify-between items-center border rounded px-4 py-3">
              <span className="truncate">
                {beautifyFileName(getFileNameFromUrl(existingPdfUrl))}
              </span>
              <button type="button" onClick={() => setIsEditingPdf(true)}>
                <img src="/icons/x-close.svg" alt="" />
              </button>
            </div>
          ) : (
            <DragDrop
              label={
                form.pdfFile ? form.pdfFile.name : "Kéo thả hoặc chọn file PDF"
              }
              acceptedFormats={["pdf"]}
              maxFiles={1}
              onFileChange={(f) => setField("pdfFile", f?.[0] || null)}
            />
          )}

          {errors.pdfFile && (
            <span className="text-xs text-red-500">{errors.pdfFile}</span>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default ProcedureFormPopup;
